import React, {useEffect,useRef} from "react";
import { historySummary, selectedHistory } from "../helpers/teamAnalysis";
import {
  historicalHomeProfile,
  homeAdvantage,
} from "../helpers/teamSettingsImpact";
export default function TeamSettingsFocusReview({
  data,
  id,
  draft,
  ranking,
  metric,
  probabilities,
  intent,
  en,
}) {
  const section=useRef(null);
  useEffect(()=>{if(intent?.count)section.current?.scrollIntoView({block:"start",behavior:"smooth"});},[intent]);
  const t = (d, e) => (en ? e : d),
    fmt = (n) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 3,
          })
        : "—";
  const home = metric === "home_advantage";
  const sources = [
    ["real", t("Reale Ergebnisse", "Actual results")],
    ["result", t("Ergebnismodell · erwartet", "Results model · expected")],
    [
      "statistics",
      t("Statistikmodell · erwartet", "Statistics model · expected"),
    ],
    ["should", "Should Goals"],
    ["market", t("Marktrückrechnung", "Market inversion")],
  ];
  const actualHome = historicalHomeProfile(data.observations, id, "real");
  const resultHome = homeAdvantage(data.result_teams?.[id]);
  const statHome = homeAdvantage(
    data.ranking?.find((r) => r.team_id === Number(id))
  );
  const matchedDraws = (key) => {
    const games = selectedHistory(data.observations, id, false).filter(
      (f) => historySummary([f], id, key).outcomes
    );
    return {
      expected: historySummary(games, id, key).outcomes,
      actual: historySummary(games, id, "real").outcomes,
    };
  };
  return (
    <section ref={section} className="ta-section">
      <h3>
        {home
          ? t("Heimvorteil prüfen", "Review home advantage")
          : t("Remis-Faktor prüfen", "Review draw factor")}
      </h3>
      {home ? (
        <>
          <p>
            {t("Gespeicherter Heimvorteil", "Saved home advantage")}:{" "}
            {t("Ergebnismodell", "Results model")} {fmt(resultHome)},{" "}
            {t("Statistikmodell", "Statistics model")} {fmt(statHome)}.{" "}
            {t("Die reale Bilanz zeigt", "Actual results show")}{" "}
            {fmt(actualHome.advantage)} {t("aus", "from")}{" "}
            {actualHome.home.count} {t("Heim- und", "home and")}{" "}
            {actualHome.away.count}{" "}
            {t(
              "Auswärtsspielen. Diese Abweichung ist ein Prüfhinweis, kein Beleg für eine dauerhafte Änderung.",
              "away matches. This difference warrants inspection, not proof of a lasting change."
            )}
          </p>
          <p>
            {t(
              "Heimvorteil = offensiver Heimfaktor / defensiver Heimfaktor. Jeder Heimfaktor bezieht sich auf das gleich gewichtete Mittel aus Heim und Auswärts.",
              "Home advantage = attacking home factor / defensive home factor. Each home factor refers to the equally weighted home and away mean."
            )}
          </p>
          <div className="ta-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("Grundlage", "Basis")}</th>
                  <th>{t("Heim für", "Home for")}</th>
                  <th>{t("Heim gegen", "Home against")}</th>
                  <th>{t("Auswärts für", "Away for")}</th>
                  <th>{t("Auswärts gegen", "Away against")}</th>
                  <th>{t("Heimvorteil", "Home advantage")}</th>
                  <th>{t("Spiele H/A", "Games H/A")}</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(([key, label]) => {
                  const r = historicalHomeProfile(data.observations, id, key);
                  return (
                    <tr key={key}>
                      <th>{label}</th>
                      {[
                        "for_home",
                        "against_home",
                        "for_away",
                        "against_away",
                        "advantage",
                      ].map((k) => (
                        <td key={k}>{fmt(r[k])}</td>
                      ))}
                      <td>
                        {r.home.count}/{r.home.selected} · {r.away.count}/
                        {r.away.selected}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p>
            {t(
              "Wenige Heim- oder Auswärtsspiele und unterschiedliche Gegner können den Vergleich stark beeinflussen. Die Bilanz allein begründet keine automatische Änderung.",
              "Small home or away samples and different opponents can strongly affect this comparison. Results alone do not justify an automatic adjustment."
            )}
          </p>
        </>
      ) : (
        <>
          <p>
            {t(
              "Der Team-Remisfaktor wirkt im Ergebnismodell über Teamfaktor × Gegnerfaktor / Ligafaktor. Das Statistikmodell verwendet dieselbe Verknüpfung und dieselbe vorhandene Remisfunktion.",
              "The results model uses team factor × opponent factor / league factor. The statistics model uses the same relation and the same existing draw function."
            )}
          </p>
          <p>{t(
            "Niedrigerer Remisfaktor → mehr Remisen. Höherer Remisfaktor → weniger Remisen. Bei gleichen übrigen Werten; an den Wahrscheinlichkeitsgrenzen ist keine weitere Änderung möglich.",
            "Lower draw factor → more draws. Higher draw factor → fewer draws. With other values unchanged; probability limits can prevent further changes."
          )}</p>
          <table>
            <thead>
              <tr>
                <th>{t("Grundlage", "Basis")}</th>
                <th>{t("Remis", "Draws")}</th>
                <th>{t("Anteil", "Rate")}</th>
                <th>
                  {t(
                    "Reale Remis · gleiche Spiele",
                    "Actual draws · same games"
                  )}
                </th>
                <th>n</th>
              </tr>
            </thead>
            <tbody>
              {sources.map(([key, label]) => {
                const h = historySummary(
                    selectedHistory(data.observations, id, false),
                    id,
                    key
                  ),
                  o = h.outcomes;
                return (
                  <tr key={key}>
                    <th>{label}</th>
                    <td>{fmt(o?.draw)}</td>
                    <td>{o ? fmt((100 * o.draw) / o.count) + " %" : "—"}</td>
                    <td>{fmt(matchedDraws(key).actual?.draw)}</td>
                    <td>
                      {o?.count ?? 0}/{h.selected}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div>
            {["result", "statistics"].map((key) => {
              const m = matchedDraws(key);
              return (
                <p key={key}>
                  {key === "result"
                    ? t("Ergebnismodell", "Results model")
                    : t("Statistikmodell", "Statistics model")}
                  :{" "}
                  {m.expected && m.actual ? (
                    <>
                      {fmt(m.expected.draw)}{" "}
                      {t("Remis erwartet,", "draws expected,")}{" "}
                      {fmt(m.actual.draw)} {t("eingetreten in", "observed in")}{" "}
                      {m.expected.count}{" "}
                      {t("vergleichbaren Spielen.", "comparable matches.")}{" "}
                      {m.actual.draw > m.expected.draw
                        ? t(
                            "Mehr Remis als erwartet: einen niedrigeren Remisfaktor als Entwurf prüfen und die Vorschau vergleichen.",
                            "More draws than expected: try a lower draw factor as a draft and compare the preview."
                          )
                        : m.actual.draw < m.expected.draw
                        ? t(
                            "Weniger Remis als erwartet: einen höheren Remisfaktor als Entwurf prüfen und die Vorschau vergleichen.",
                            "Fewer draws than expected: try a higher draw factor as a draft and compare the preview."
                          )
                        : t(
                            "Keine Abweichung der Remis-Anzahl.",
                            "No difference in the draw count."
                          )}
                    </>
                  ) : (
                    t("Vergleichsdaten fehlen.", "Comparison data missing.")
                  )}
                </p>
              );
            })}
          </div>
          <p>
            {t(
              "Einzelne Remis sind bei kleinen Stichproben kein verlässlicher Kalibrierungsmaßstab. Kleine manuelle Schritte prüfen; ein fachlich kalibrierter automatischer Remisvorschlag liegt nicht vor.",
              "A few draws in a small sample are not a reliable calibration basis. Inspect small manual steps; there is no calibrated automatic draw recommendation."
            )}
          </p>
        </>
      )}
      <h4>
        {t(
          "Entwurf · Heimspiel gegen Ligamittel",
          "Draft · home match against league mean"
        )}
      </h4>
      <p>
        {t(
          "Illustration mit aktueller Modellrechnung, keine Änderung historischer Erwartungen.",
          "Illustration using the current model calculation, not a change to historical expectations."
        )}
      </p>
      <table>
        <thead>
          <tr>
            <th>{t("Modell", "Model")}</th>
            <th>{t("Tore für / gegen", "Goals for / against")}</th>
            <th>{t("Remis vorher → Entwurf", "Draw before → draft")}</th>
            <th>{t("Verwendeter Remisfaktor", "Applied draw factor")}</th>
          </tr>
        </thead>
        <tbody>
          {["result", "statistics"].map((model) => {
            const a = probabilities?.before?.[id]?.[model],
              b = probabilities?.after?.[id]?.[model];
            return (
              <tr key={model}>
                <th>
                  {model === "result"
                    ? t("Ergebnismodell", "Results model")
                    : t("Statistikmodell", "Statistics model")}
                </th>
                <td>
                  {fmt(a?.home)} / {fmt(a?.away)} → {fmt(b?.home)} /{" "}
                  {fmt(b?.away)}
                </td>
                <td>
                  {fmt(a?.drawprob == null ? null : 100 * a.drawprob)} % →{" "}
                  {fmt(b?.drawprob == null ? null : 100 * b.drawprob)} %
                </td>
                <td>
                  {fmt(a?.drawfactor)} → {fmt(b?.drawfactor)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
